/**
 * Estilos compartilhados das novas sub-telas de Acessos.
 *
 * Sprint C da migracao RBAC: o arquivo antigo `AcessosLegado/styles.jsx`
 * foi removido junto com a tela legada. `Button` e `Message` continuam
 * exportados aqui porque componentes externos (src/components/Agendamento)
 * ainda os consomem — definidos inline abaixo.
 */
import styled, { css } from "styled-components";

import { getToneTokens } from "../../styles/shared";

export const Button = styled.button`
  min-height: 38px;
  border-radius: 8px;
  border: 1px solid ${({ theme, $variant }) =>
    $variant === "secondary" ? theme.border : theme.accent};
  background: ${({ theme, $variant }) =>
    $variant === "secondary" ? theme.surface : theme.accent};
  color: ${({ theme, $variant }) =>
    $variant === "secondary" ? theme.textSecondary : "var(--ink-on-accent)"};
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 8px 11px;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.accent};
    color: ${({ theme, $variant }) =>
      $variant === "secondary" ? theme.accent : "var(--ink-on-accent)"};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const Message = styled.div`
  ${({ theme, $tone = "info" }) => {
    const tokens = getToneTokens(theme, $tone);
    return css`
      background: ${tokens.background};
      border: 1px solid ${tokens.border};
      color: ${tokens.text};
    `;
  }}

  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
  overflow-wrap: anywhere;
`;

export const SubNav = styled.nav`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 4px;
  background: ${({ theme }) => theme.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 10px;
`;

export const SubNavLink = styled.button`
  appearance: none;
  background: ${({ theme, $active }) =>
    $active ? theme.surface : "transparent"};
  border: 0;
  padding: 6px 14px;
  font-size: 0.85rem;
  border-radius: 8px;
  color: ${({ theme, $active }) =>
    $active ? theme.textPrimary : theme.textSecondary};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

export const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) 1fr;
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  border-radius: 16px;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const CardHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

export const CardTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary};
`;

export const CardSubtitle = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.textSecondary};
`;

export const SearchBar = styled.input`
  width: 100%;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.9rem;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.88rem;

  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }

  th {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.textSecondary};
    font-weight: 600;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  tbody tr {
    cursor: ${({ $rowsClickable }) => ($rowsClickable ? "pointer" : "default")};
  }
`;

export const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ theme, $tone = "neutral" }) =>
    getToneTokens(theme, $tone).background};
  color: ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).text};
  border: 1px solid
    ${({ theme, $tone = "neutral" }) => getToneTokens(theme, $tone).border};
  white-space: nowrap;
`;

export const ActionsBar = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

export const PrimaryButton = styled.button`
  background: ${({ theme }) => theme.accent};
  color: var(--ink-on-accent);
  border: 0;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
  }
`;

export const SecondaryButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.textPrimary};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: ${({ theme }) => theme.surfaceAlt};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const DangerButton = styled(SecondaryButton)`
  color: ${({ theme }) => theme.dangerText};
  border-color: ${({ theme }) => theme.dangerBorder};

  &:hover {
    background: ${({ theme }) => theme.dangerSurface};
  }
`;

export const ThreeZoneLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(260px, 320px) 1fr;
  gap: 16px;
  align-items: start;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

export const ContextRail = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: sticky;
  top: 90px;

  @media (max-width: 980px) {
    position: static;
  }
`;

export const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const InlineForm = styled.form`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;

  > .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const Input = styled.input`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.9rem;
  font-weight: 400;
  text-transform: none;

  &:focus {
    outline: 2px solid ${({ theme }) => theme.accent};
    outline-offset: 1px;
  }
`;

export const Textarea = styled.textarea`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.9rem;
  font-weight: 400;
  text-transform: none;
  resize: vertical;
  min-height: 64px;
`;

export const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 0.9rem;
  font-weight: 400;
  text-transform: none;
`;

export const EmptyMsg = styled.p`
  margin: 0;
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.textSecondary};
  font-size: 0.9rem;
`;

export const ErrorMsg = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.dangerBorder};
  background: ${({ theme }) => theme.dangerSurface};
  color: ${({ theme }) => theme.dangerText};
  font-size: 0.85rem;
`;

export const InfoMsg = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.infoBorder};
  background: ${({ theme }) => theme.infoSurface};
  color: ${({ theme }) => theme.infoText};
  font-size: 0.85rem;
`;
